import { useState } from 'react';
import { Plus } from 'lucide-react';
import type { WarehousePackage, AddPackageRequest, ShippingDocument, ShippingDocumentType } from '@/apis/warehouse';
import { useAddPackage, useUploadShippingDocument, useDeleteShippingDocument } from '@/hooks/useWarehouse';
import { AddPackageDialog } from './AddPackageDialog';
import { ShippingDocumentSlot } from './ShippingDocumentSlot';

const DOCUMENT_SLOTS: { type: ShippingDocumentType; label: string }[] = [
  { type: 'invoice', label: 'Invoice' },
  { type: 'label', label: 'Label' },
  { type: 'bill_of_lading', label: 'Bill of Lading' },
];

interface Props {
  orderId: string;
  packages: WarehousePackage[];
  shippingDocuments?: Partial<Record<ShippingDocumentType, ShippingDocument>>;
  canAct?: boolean;
}

export function PackagesSection({ orderId, packages, shippingDocuments, canAct = true }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const addPackage = useAddPackage(orderId);
  const uploadDocument = useUploadShippingDocument(orderId);
  const deleteDocument = useDeleteShippingDocument(orderId);
  const [pendingType, setPendingType] = useState<ShippingDocumentType | null>(null);

  const handleSave = (data: AddPackageRequest) => {
    addPackage.mutate(data, { onSuccess: () => setDialogOpen(false) });
  };

  const handleUpload = (type: ShippingDocumentType, file: File) => {
    setPendingType(type);
    uploadDocument.mutate({ type, file }, { onSettled: () => setPendingType(null) });
  };

  const handleDelete = (type: ShippingDocumentType) => {
    setPendingType(type);
    deleteDocument.mutate(type, { onSettled: () => setPendingType(null) });
  };

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-black">Packages</h2>
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          disabled={!canAct}
          className="flex items-center gap-1.5 rounded-full border border-smoke px-3 py-1.5 text-sm font-normal text-lightGray hover:bg-smoke1 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus size={14} />
          Add package
        </button>
      </div>
      {packages.length === 0 ? (
        <p className="text-sm text-lightGray">No packages recorded yet.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {packages.map((pkg, index) => (
            <div key={pkg.id} className="rounded-2xl border border-smoke bg-white p-4">
              <p className="font-semibold text-black">Package {index + 1}</p>
              <p className="mt-1 text-sm text-lightGray">
                {pkg.largestDimension}x{pkg.middleDimension}x{pkg.smallestDimension} in.
              </p>
              <p className="text-sm text-lightGray">{pkg.weight} lbs.</p>
              {pkg.note && <p className="mt-2 text-sm text-lightGray">Package comment: {pkg.note}</p>}
            </div>
          ))}
        </div>
      )}

      {packages.length > 0 && (
        <div className="mt-4 rounded-2xl border border-smoke bg-white p-4">
          <p className="mb-3 text-sm font-semibold text-black">Shipping Documents</p>
          <div className="flex flex-wrap gap-5">
            {DOCUMENT_SLOTS.map(({ type, label }) => (
              <ShippingDocumentSlot
                key={type}
                label={label}
                document={shippingDocuments?.[type]}
                onUpload={(file) => handleUpload(type, file)}
                onDelete={() => handleDelete(type)}
                isUploading={uploadDocument.isPending && pendingType === type}
                isDeleting={deleteDocument.isPending && pendingType === type}
                canAct={canAct}
              />
            ))}
          </div>
        </div>
      )}

      <AddPackageDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={handleSave}
        isPending={addPackage.isPending}
      />
    </section>
  );
}
